import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Stack, Grid, Select, MenuItem, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Skeleton, Tooltip,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import * as XLSX from 'xlsx';
import { analyticsApi } from '../services/api';
import { ErrorState } from './common/States';
import EmptyState from './common/EmptyState';
import SubTabs from './common/SubTabs';
import DeltaBadge from './common/DeltaBadge';
import { addDays, apiError, formatDate, formatDateShort, formatMoney as formatMoneyBase, mskToday } from '../utils/format';
import { mpPath } from '../utils/marketplaces';
import ExportGate from './common/ExportGate';
import { tokens } from '../theme';
import { toneColor } from '../utils/tone';

// Все суммы в аналитике — с копейками, как в отчётах WB.
const formatMoney = (v, opts) => formatMoneyBase(v, { kopecks: true, ...opts });

const COLS = [
  { key: 'realized', label: 'Продажи' },
  { key: 'for_pay', tone: 'signed', label: 'К перечислению за товар' },
  { key: 'logistics', tone: 'expense', label: 'Логистика' },
  { key: 'storage', tone: 'expense', label: 'Хранение' },
  { key: 'penalty', tone: 'expense', label: 'Штрафы' },
  { key: 'bank_payment', tone: 'signed', label: 'Итого к оплате' },
];

// WB's own figure when it sent one, otherwise the one MARQIT computed from the report rows.
const value = (r, key) => {
  const c = r.checks.find((x) => x.key === key);
  if (!c) return null;
  return Number(c.wb ?? c.computed);
};

// Weekly realization reports of Wildberries, loaded automatically by the sync.
const WeeklyReports = ({ accounts }) => {
  const navigate = useNavigate();
  const [weeks, setWeeks] = useState('12');
  const [accountId, setAccountId] = useState('');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    setRows(null);
    const to = mskToday();
    const from = addDays(to, -(Number(weeks) * 7 - 1));
    try {
      const { data } = await analyticsApi.reconciliation({ from, to, ...(accountId ? { account_id: accountId } : {}) });
      setRows(data.filter((r) => r.period === 'weekly'));
    } catch (e) {
      setError(apiError(e));
    }
  }, [weeks, accountId]);
  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    if (!rows?.length) return null;
    const sum = (k) => rows.reduce((s, r) => s + (value(r, k) || 0), 0);
    const last = rows[0];
    const prev = rows[1];
    const lp = value(last, 'bank_payment');
    const pp = prev ? value(prev, 'bank_payment') : null;
    return {
      last, lastPay: lp, delta: pp ? ((lp - pp) / Math.abs(pp)) * 100 : null,
      realized: sum('realized'), pay: sum('bank_payment'), mismatches: rows.filter((r) => !r.matches).length,
    };
  }, [rows]);

  const exportXlsx = () => {
    const header = ['Неделя с', 'по', '№ отчёта', ...COLS.map((c) => c.label), 'Сверка'];
    const body = rows.map((r) => [r.date_from, r.date_to, r.report_id, ...COLS.map((c) => value(r, c.key)), r.matches ? 'совпадает' : 'расхождение']);
    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Еженедельные отчёты');
    XLSX.writeFile(wb, `marqit_wb_еженедельные_отчёты_${mskToday()}.xlsx`);
  };

  const open = (r) => navigate(`${mpPath('wildberries')}?from=${r.date_from}&to=${r.date_to}${accountId ? `&account=${accountId}` : ''}`);

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2.5 }}>
        {accounts.length > 1 && (
          <Select size="small" value={accountId || 'all'} onChange={(e) => setAccountId(e.target.value === 'all' ? '' : e.target.value)} inputProps={{ 'aria-label': 'Кабинет' }} sx={{ minWidth: 220 }}>
            <MenuItem value="all">Все кабинеты</MenuItem>
            {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
          </Select>
        )}
        <SubTabs value={weeks} onChange={setWeeks} items={[{ key: '4', label: '4 недели' }, { key: '8', label: '8 недель' }, { key: '12', label: '12 недель' }]} />
        <Box sx={{ flex: 1 }} />
        <ExportGate><Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={exportXlsx} disabled={!rows?.length}>Скачать XLSX</Button></ExportGate>
      </Stack>

      {error && <ErrorState message={error} onRetry={load} />}
      {!rows && !error && <Skeleton variant="rounded" height={360} sx={{ borderRadius: '16px' }} />}
      {rows && rows.length === 0 && (
        <Card><CardContent>
          <EmptyState title="Еженедельных отчётов пока нет" description="Отчёты WB загружаются автоматически раз в час. Если кабинет подключён недавно, они появятся после первой загрузки." />
        </CardContent></Card>
      )}

      {summary && (
        <>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} md={5}>
              <Card sx={{ height: '100%', background: tokens.heroGradient }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Последняя неделя · {formatDateShort(summary.last.date_from)} — {formatDateShort(summary.last.date_to)}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '2rem', lineHeight: 1.25, color: toneColor(summary.lastPay) }}>{formatMoney(summary.lastPay)}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography color="text.secondary">итого к оплате</Typography>
                    <DeltaBadge value={summary.delta} suffix={summary.delta != null ? 'к прошлой неделе' : null} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3.5}>
              <Card sx={{ height: '100%' }}><CardContent>
                <Typography variant="body2" color="text.secondary">Продажи за {rows.length} нед.</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1.5rem' }}>{formatMoney(summary.realized)}</Typography>
                <Typography variant="body2" color="text.secondary">к оплате {formatMoney(summary.pay)}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} md={3.5}>
              <Card sx={{ height: '100%' }}><CardContent>
                <Typography variant="body2" color="text.secondary">Сверка с итогами WB</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  {summary.mismatches ? <WarningAmberRoundedIcon sx={{ color: tokens.warning }} /> : <CheckCircleRoundedIcon sx={{ color: tokens.success }} />}
                  <Typography sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
                    {summary.mismatches ? `Расхождений: ${summary.mismatches}` : 'Всё совпадает'}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">суммы MARQIT и WB по каждому отчёту</Typography>
              </CardContent></Card>
            </Grid>
          </Grid>

          <Card>
            <Box sx={{ overflowX: 'auto' }}>
              <Table aria-label="Еженедельные отчёты Wildberries">
                <TableHead>
                  <TableRow>
                    <TableCell>Неделя</TableCell>
                    {COLS.map((c) => <TableCell key={c.key} align="right">{c.label}</TableCell>)}
                    <TableCell>Сверка</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={`${r.report_id}`} hover onClick={() => open(r)} sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(r.date_from)} — {formatDate(r.date_to)}</Typography>
                        <Typography variant="caption" color="text.secondary">отчёт № {r.report_id}</Typography>
                      </TableCell>
                      {COLS.map((c) => (
                        <TableCell key={c.key} align="right"
                          sx={{ whiteSpace: 'nowrap', fontWeight: c.key === 'bank_payment' ? 700 : 400, ...(c.tone && { color: toneColor(value(r, c.key), c.tone) }) }}>
                          {formatMoney(value(r, c.key))}
                        </TableCell>
                      ))}
                      <TableCell>
                        {r.matches
                          ? <Chip size="small" label="совпадает" sx={{ bgcolor: tokens.successSoft, color: tokens.success }} />
                          : (
                            <Tooltip title="Суммы, рассчитанные по строкам отчёта, отличаются от итогов WB. Подробности — в «Аналитике» на вкладке «Сверка с WB».">
                              <Chip size="small" label="расхождение" sx={{ bgcolor: tokens.warningSoft, color: tokens.warning }} />
                            </Tooltip>
                          )}
                      </TableCell>
                      <TableCell align="right"><ChevronRightRoundedIcon sx={{ color: tokens.inkMuted }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Card>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Нажмите на неделю, чтобы открыть её разбор: расходы, товары, штрафы. Отчёты загружаются из WB автоматически.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default WeeklyReports;
